import { useEffect, useRef, useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import cytoscape from 'cytoscape'
import { Maximize, Maximize2, Minimize2, Download, Loader2, AlertCircle } from 'lucide-react'
import { getFolderConnections } from '../../services/api'
import { COSE_BASE_LAYOUT, COSE_SPREAD_LAYOUT } from '../graph/cytoscapeLayoutConfig'
import type { FolderConnectionsData } from '../../types'

interface FolderConnectionsGraphProps {
  projectId: string
  folderId: string
  selectedDocIds: string[]
}

const NODE_COLORS: Record<string, string> = {
  document: '#0EA5E9',      // iflow-500 blue
  journal: '#F59E0B',       // amber-500
  contact: '#22C55E',       // green-500
  organisation: '#A855F7',  // purple-500
  topic: '#9CA3AF',         // gray-400
}

const NODE_SHAPES: Record<string, string> = {
  document: 'rectangle',
  journal: 'ellipse',
  contact: 'diamond',
  organisation: 'hexagon',
  topic: 'ellipse',
}

export default function FolderConnectionsGraph({ projectId, folderId, selectedDocIds }: FolderConnectionsGraphProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const cyRef = useRef<cytoscape.Core | null>(null)
  const [depth, setDepth] = useState(1)
  const [mode, setMode] = useState<'related' | 'saved' | 'both'>('both')
  const [fullscreen, setFullscreen] = useState(false)
  // CoWork Round 3 (#2785 follow-up): Spread re-runs the cose layout with
  // higher repulsion so dense centre clusters spread out.
  // spreadCounter triggers a re-mount of the layout if cy was destroyed.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_spreadCounter, setSpreadCounter] = useState(0)
  const handleSpread = useCallback(() => {
    if (cyRef.current) {
      cyRef.current.layout(COSE_SPREAD_LAYOUT as cytoscape.LayoutOptions).run()
    }
    setSpreadCounter((c) => c + 1)  // trigger re-mount of layout if cy is gone
  }, [])

  const sources = selectedDocIds.length > 0 ? selectedDocIds : undefined

  const { data, isLoading, isError } = useQuery({
    queryKey: ['folder-connections', projectId, folderId, depth, sources, mode],
    queryFn: () => getFolderConnections(projectId, folderId, depth, sources, mode),
    enabled: !!projectId && !!folderId,
  })

  const renderGraph = useCallback((graphData: FolderConnectionsData) => {
    if (!containerRef.current) return
    if (graphData.nodes.length === 0) return

    /* istanbul ignore next -- cleanup guard for unmount */
    if (cyRef.current) {
      cyRef.current.destroy()
    }

    const elements: cytoscape.ElementDefinition[] = []

    for (const node of graphData.nodes) {
      elements.push({
        data: {
          id: node.id,
          label: node.label,
          nodeType: node.type,
          score: node.score ?? 1,
        },
      })
    }

    for (const edge of graphData.edges) {
      elements.push({
        data: {
          source: edge.source,
          target: edge.target,
          weight: edge.weight,
          saved: edge.saved,
        },
      })
    }

    const cy = cytoscape({
      container: containerRef.current,
      elements,
      style: [
        {
          selector: 'node',
          style: {
            // CoWork Round 3 (Story #2777 #2785 follow-up): the FOLDER-level
            // graph (not ConnectionsGraph) is what InfinityWheel actually
            // renders. R2's fixes only touched ConnectionsGraph; QA was
            // looking at this component the whole time. Mirror the same
            // legibility settings here:
            //   font-size 10 → 12, weight 600
            //   text-wrap 'ellipsis' → 'wrap' (no `…` truncation)
            //   text-max-width 60 → 140 (room for 2-line wraps)
            //   background-opacity 0.7 → 0.95 (opaque, not see-through)
            //   border for tighter contrast against canvas
            label: 'data(label)',
            'font-size': '12px',
            'font-weight': 600,
            'text-wrap': 'wrap',
            'text-max-width': '140px',
            'text-background-color': '#ffffff',
            'text-background-opacity': 0.95,
            'text-background-padding': '3px',
            'text-background-shape': 'roundrectangle',
            'text-border-color': '#E5E7EB',
            'text-border-opacity': 1,
            'text-border-width': 0.5,
            width: 30,
            height: 30,
            'background-color': (ele: cytoscape.NodeSingular) => NODE_COLORS[ele.data('nodeType')] || '#9CA3AF',
            shape: (ele: cytoscape.NodeSingular) => (NODE_SHAPES[ele.data('nodeType')] || 'ellipse') as cytoscape.Css.NodeShape,
            color: '#1F2937',
            'text-halign': 'center',
            'text-valign': 'bottom',
            'text-margin-y': 4,
          } as cytoscape.Css.Node,
        },
        {
          selector: 'node[nodeType = "topic"]',
          style: {
            width: 15,
            height: 15,
            'font-size': '8px',
          },
        },
        {
          selector: 'node[score < 0.4]',
          style: {
            'border-width': 2,
            'border-style': 'dashed',
            'border-color': '#9CA3AF',
            opacity: 0.55,
          } as cytoscape.Css.Node,
        },
        {
          selector: 'edge',
          style: {
            width: (ele: cytoscape.EdgeSingular) => Math.max(1, (ele.data('weight') || 0.5) * 4),
            'line-color': '#D1D5DB',
            'target-arrow-color': '#D1D5DB',
            'target-arrow-shape': 'triangle',
            'curve-style': 'bezier',
            'line-style': (ele: cytoscape.EdgeSingular) => (ele.data('saved') ? 'solid' : 'dashed') as cytoscape.Css.LineStyle,
          } as cytoscape.Css.Edge,
        },
        {
          selector: 'edge[saved]',
          style: {
            'line-color': '#0EA5E9',
            'target-arrow-color': '#0EA5E9',
          },
        },
        {
          selector: 'edge[type = "same_folder"]',
          style: {
            'line-color': '#D1D5DB',
            'target-arrow-color': '#D1D5DB',
            'line-style': 'dashed' as cytoscape.Css.LineStyle,
            width: 1,
            opacity: 0.5,
          } as cytoscape.Css.Edge,
        },
      ],
    })

    cyRef.current = cy

    cy.layout(COSE_BASE_LAYOUT as cytoscape.LayoutOptions).run()

    // Hover tooltip for truncated labels
    const tooltipDiv = document.createElement('div')
    tooltipDiv.style.cssText = 'display:none;position:absolute;z-index:10;background:#fff;border:1px solid #d1d5db;border-radius:6px;padding:6px 10px;font-size:12px;color:#374151;max-width:300px;word-wrap:break-word;box-shadow:0 2px 8px rgba(0,0,0,0.1);pointer-events:none;'
    containerRef.current?.appendChild(tooltipDiv)

    cy.on('mouseover', 'node', (evt) => {
      const node = evt.target
      tooltipDiv.textContent = node.data('label') || ''
      tooltipDiv.style.display = 'block'
      const pos = node.renderedPosition()
      tooltipDiv.style.left = `${pos.x + 20}px`
      tooltipDiv.style.top = `${pos.y - 10}px`
    })
    cy.on('mouseout', 'node', () => {
      tooltipDiv.style.display = 'none'
    })
  }, [])

  useEffect(() => {
    let rafId: number
    if (data) {
      rafId = requestAnimationFrame(() => {
        renderGraph(data)
      })
    }
    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      if (cyRef.current) {
        cyRef.current.destroy()
        cyRef.current = null
      }
    }
  }, [data, renderGraph])

  const handleExportPng = () => {
    if (!cyRef.current) return
    const png = cyRef.current.png({ full: true, scale: 2, bg: '#ffffff' })
    const link = document.createElement('a')
    link.href = png
    link.download = 'connections-graph.png'
    link.click()
  }

  return (
    <div className={`space-y-3 ${fullscreen ? 'fixed inset-0 z-50 bg-white p-4 flex flex-col' : ''}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          {/* Depth selector */}
          <div className="flex items-center gap-1.5 text-sm">
            <span className="text-gray-600">Depth:</span>
            {[1, 2, 3, 4, 5].map((d) => (
              <button
                key={d}
                onClick={() => setDepth(d)}
                className={`w-7 h-7 rounded text-xs font-medium ${
                  depth === d ? 'bg-iflow-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {d}
              </button>
            ))}
          </div>

          {/* Mode selector */}
          <div className="flex border border-gray-300 rounded-lg overflow-hidden text-xs">
            {(['related', 'saved', 'both'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`px-2.5 py-1 capitalize ${mode === m ? 'bg-iflow-700 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                {m === 'related' ? 'Related Journals' : m === 'saved' ? 'Saved List' : 'Both'}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {data && (
            <span className="text-xs text-gray-500">
              {data.node_count} nodes, {data.edge_count} edges
            </span>
          )}
          {/* CoWork Round 3 (#2785 follow-up): Spread button. */}
          <button
            onClick={handleSpread}
            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-800 border border-gray-200 rounded hover:bg-gray-50"
            title="Re-run layout — spreads dense clusters out so labels don't overlap"
            data-testid="folder-connections-spread-button"
          >
            <Maximize className="w-4 h-4" /> Spread
          </button>
          <button onClick={handleExportPng} className="p-1.5 text-gray-500 hover:text-gray-600 rounded" title="Export PNG">
            <Download className="w-4 h-4" />
          </button>
          <button
            onClick={() => setFullscreen(!fullscreen)}
            className="p-1.5 text-gray-500 hover:text-gray-600 rounded"
            title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
        {Object.entries(NODE_COLORS).map(([type, color]) => (
          <div key={type} className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
            <span className="capitalize">{type}</span>
          </div>
        ))}
        <span className="mx-1">|</span>
        <span>— Saved</span>
        <span>- - Suggested</span>
      </div>

      {/* Graph wrapper — relative container for absolute positioning */}
      <div className={`relative border border-gray-200 rounded-lg bg-gray-50 ${fullscreen ? 'flex-1' : 'h-96'}`}>
        {/* Cytoscape container — NO React children allowed */}
        <div ref={containerRef} className="absolute inset-0" />

        {/* Overlays — siblings of containerRef, not children */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
          </div>
        )}
        {!isLoading && isError && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 text-red-500 text-sm">
            <AlertCircle className="w-5 h-5" />
            <span>Failed to load connections graph. Please try again.</span>
          </div>
        )}
        {!isLoading && !isError && data && data.nodes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm">
            No connections found. Upload and embed documents to see their connections.
          </div>
        )}
      </div>

      {selectedDocIds.length === 0 && (
        <p className="text-xs text-gray-500">Showing connections for all documents in folder. Select documents above to filter.</p>
      )}
    </div>
  )
}
